import { MutableRefObject, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AbsenceCategory, User } from "@myapp/contract";
import { FormSection } from "../../../../shared/ui/controls/FormSection/FormSection";
import { FieldWrapper } from "../../../../shared/ui";
import { FormInput } from "../../../../shared/ui/controls/FormInput";
import { EntityMultiSelect } from "../../../../shared/ui/search/EntityMultiSelect/EntityMultiSelect";
import { useUsersInfiniteQuery } from "../../../../entities/user";
import {
  isFullDayCategory,
  timeManagementFormSchema,
  getDefaultValues,
  type TimeManagementFormValues,
} from "../model/timeManagementForm.schema";

export type { TimeManagementFormValues };

interface Props {
  category: AbsenceCategory;
  onSubmit: (values: TimeManagementFormValues) => void;
  isLoading: boolean;
  submitRef: MutableRefObject<(() => void) | null>;
  initialValues?: TimeManagementFormValues;
  hideTechnicianSelect?: boolean;
  readOnly?: boolean;
}

export function TimeManagementFormWidget({
  category,
  onSubmit,
  isLoading,
  submitRef,
  initialValues,
  hideTechnicianSelect = false,
  readOnly = false,
}: Props) {
  const isFullDay = isFullDayCategory(category);

  /**
   * `readOnly` on its own is not enough to lock these fields: Chrome still opens the picker on a
   * readonly `<input type="date">` / `type="time"` and lets it write a new value, and the three
   * date/time fields here are most of the form. Disabling as well is what actually makes it
   * read-only — and it is the only thing that greys the control, so it also *looks* locked.
   *
   * Safe for the values: the form is controlled by react-hook-form and never submitted natively,
   * so disabled inputs still carry their data.
   */
  const lock = { readOnly, disabled: readOnly } as const;

  const { control, handleSubmit } = useForm<TimeManagementFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(timeManagementFormSchema) as any,
    defaultValues: initialValues ?? getDefaultValues(category),
  });

  /**
   * The submit handler, published to the host page — `PageLayout.primaryAction` is the button, and
   * it lives outside this component.
   *
   * Assigned in an effect rather than during render, which is where it used to happen: writing to a
   * ref in the render body is a side effect, and under StrictMode or a discarded concurrent render
   * it runs for a tree that is never committed. `UserFormWidget` already does it this way.
   */
  const submit = handleSubmit(
    (data) => {
      if (!isLoading) onSubmit(data);
    },
    (errors) => {
      console.error("[TimeManagementForm] validation errors:", errors);
    },
  );

  // A read-only form publishes no submit handler at all. The host page hides its Speichern button,
  // but Enter inside the form would otherwise still reach `submit` and fire the mutation.
  useEffect(() => {
    submitRef.current = readOnly ? null : submit;
  });

  return (
    // Submits on Enter as well as from the host page's button. Both land on the same handler.
    <form
      className="flex flex-col gap-5 sm:flex-row sm:gap-10"
      onSubmit={readOnly ? (event) => event.preventDefault() : submit}
      noValidate
    >
      {!hideTechnicianSelect && (
        <FormSection title="Handwerker">
          <Controller
            name="technicianIds"
            control={control}
            render={({ field, fieldState }) => (
              <div>
                <EntityMultiSelect<User>
                  value={field.value}
                  onChange={field.onChange}
                  useInfiniteHook={useUsersInfiniteQuery}
                  searchParamName="search"
                  query={{ role: "WORKER" }}
                  getLabel={(item) => item.name}
                  disabled={readOnly}
                />
                {fieldState.error && (
                  <p className="mt-1 text-sm text-red-500">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
        </FormSection>
      )}

      <FormSection title={hideTechnicianSelect ? undefined : "Details"}>
        {isFullDay ? (
          <div className="flex flex-row justify-between gap-5">
            <FieldWrapper label="Von">
              <FormInput name="dateFrom" control={control} type="date" as="input" {...lock} className="min-h-[40px]"/>
            </FieldWrapper>
            <FieldWrapper label="Bis">
              <FormInput name="dateTo" control={control} type="date" as="input" {...lock} className="min-h-[40px]"/>
            </FieldWrapper>
          </div>
        ) : (
          <>
            <FieldWrapper label="Datum">
              <FormInput name="date" control={control} type="date" as="input" {...lock} />
            </FieldWrapper>
            <div className="flex flex-row justify-between gap-5">
              <FieldWrapper label="Von">
                <FormInput name="timeFrom" control={control} type="time" as="input" {...lock} />
              </FieldWrapper>
              <FieldWrapper label="Bis">
                <FormInput name="timeTo" control={control} type="time" as="input" {...lock} />
              </FieldWrapper>
            </div>
          </>
        )}
        <FieldWrapper label="Kommentar">
          <FormInput name="comment" control={control} as="textarea" rows={3} {...lock} />
        </FieldWrapper>
      </FormSection>
    </form>
  );
}
